/**
 * El listado — `OD-0020` E-02, nivel 1.
 *
 * Sale de `GLOBAL_DB`: nombre, plan, estado, alta y shard. No es dato de
 * tenant, y por eso abrir esta pantalla **no** registra un acceso a los datos
 * de nadie. La ficha sí; eso se avisa allí.
 *
 * Una empresa con shard asignado y sin fila en el censo aparece como `UNKNOWN`
 * y sale primero: es una incoherencia que hay que ver.
 */
import { useEffect, useState } from "react";
import { api, type EmpresaResumen, type ResumenDePruebas } from "../api/cliente";
import { t } from "../textos";

function fecha(iso: string): string {
  if (iso === "") return "";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString("es-ES", { day: "2-digit", month: "2-digit", year: "numeric" });
}

/**
 * El último día de Business de una prueba (`OD-0064` D-03): el anterior a
 * `endsAt`, que es la medianoche de Madrid en que empieza Free. En hora de
 * Madrid y no en la del navegador, que es la que manda en la OD.
 */
function ultimoDia(endsAt: string): string {
  const fin = Date.parse(endsAt);
  if (Number.isNaN(fin)) return endsAt;
  return new Date(fin - 1).toLocaleDateString("es-ES", { day: "2-digit", month: "2-digit", year: "numeric", timeZone: "Europe/Madrid" });
}

/** Los estados que se pueden filtrar, y el orden en que se ofrecen. */
const FILTROS = ["TODAS", "ACTIVE", "CLOSED", "UNKNOWN"] as const;
type Filtro = (typeof FILTROS)[number];

export function Empresas({ onAbrir }: { onAbrir: (id: string) => void }) {
  const [estado, setEstado] = useState<"cargando" | "ok" | "no-encontrado" | "sin-mfa" | "error">("cargando");
  const [empresas, setEmpresas] = useState<readonly EmpresaResumen[]>([]);
  /**
   * El filtro vive en el navegador y no en la API: el listado ya viene entero
   * —son las empresas de Faktelio, no un catalogo— y filtrar en el servidor
   * obligaria a paginar dos veces sobre lo mismo.
   */
  const [filtro, setFiltro] = useState<Filtro>("TODAS");
  /**
   * `OD-0064` D-12. Aparte del listado y sin bloquearlo: sin `global/0022` la
   * ruta falla, y el listado tiene que seguir saliendo. Sin numeros, no se
   * enseña la linea.
   */
  const [pruebas, setPruebas] = useState<ResumenDePruebas | null>(null);

  useEffect(() => {
    void (async (): Promise<void> => {
      const r = await api.pruebas();
      if (r.ok) setPruebas(r.datos);
    })();
  }, []);

  useEffect(() => {
    void (async (): Promise<void> => {
      const r = await api.empresas();
      if (r.ok) {
        setEmpresas(r.datos);
        setEstado("ok");
        return;
      }
      // 404 = no es operador, o no hay backoffice. No se distingue a propósito.
      if (r.estado === 404 || r.estado === 401) setEstado("no-encontrado");
      else if (r.estado === 403) setEstado("sin-mfa");
      else setEstado("error");
    })();
  }, []);

  if (estado === "cargando") return <p className="suave">{t.comun.cargando}</p>;
  if (estado === "no-encontrado") return <p>{t.comun.noEncontrado}</p>;
  if (estado === "sin-mfa") return <p className="aviso" data-tono="aviso">{t.comun.sinMfa}</p>;
  if (estado === "error") return <p className="aviso" data-tono="error">{t.comun.error}</p>;

  const visibles = filtro === "TODAS" ? empresas : empresas.filter((e) => e.status === filtro);

  return (
    <>
      <h1>{t.empresas.titulo}</h1>
      <p className="suave">{t.empresas.resumen(empresas.length)}</p>
      {pruebas === null ? null : <p className="suave">{t.empresas.pruebas(pruebas)}</p>}
      <div className="filtros">
        {FILTROS.map((f) => (
          <button
            key={f}
            type="button"
            data-variante={filtro === f ? undefined : "secundario"}
            onClick={() => setFiltro(f)}
            aria-pressed={filtro === f}
          >
            {`${t.empresas.filtros[f]} (${f === "TODAS" ? empresas.length : empresas.filter((e) => e.status === f).length})`}
          </button>
        ))}
      </div>
      <table>
        <thead>
          <tr>
            <th>{t.empresas.columnas.nombre}</th>
            <th>{t.empresas.columnas.plan}</th>
            <th>{t.empresas.columnas.estado}</th>
            <th>{t.empresas.columnas.alta}</th>
            <th>{t.empresas.columnas.shard}</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {visibles.map((e) => (
            <tr key={e.id}>
              <td>
                {e.name === "" ? <em className="suave">{t.empresas.desconocida}</em> : e.name}
                <div className="suave">{e.id}</div>
              </td>
              <td>
                {e.planVersion}
                {e.planSinSuscripcionViva ? (
                  <div className="aviso-en-celda" title={t.empresas.sinSuscripcionVivaDetalle(e.subscriptionState)}>
                    {t.empresas.sinSuscripcionViva}
                  </div>
                ) : null}
                {e.prueba === null ? null : <div className="suave">{t.empresas.prueba(e.prueba.estado, ultimoDia(e.prueba.endsAt))}</div>}
                {/* No se sabe NO es "no tiene": una columna ciega tiene que decirlo. */}
                {e.subscriptionUnknown ? <div className="suave">{t.empresas.suscripcionSinSaber}</div> : null}
              </td>
              <td>{e.status}</td>
              <td>{fecha(e.createdAt)}</td>
              <td>{e.shard ?? ""}</td>
              <td>
                <button type="button" data-variante="secundario" onClick={() => onAbrir(e.id)}>
                  {t.empresas.verFicha}
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </>
  );
}
